/** SUNTRONIX ONE — Contracts & Entitlements workspace. */
import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  FileText, ShieldCheck, Plus, RefreshCw, ArrowLeft, CalendarClock,
  CheckCircle2, XCircle, TrendingUp,
} from "lucide-react";

const STATUS_TONE = {
  DRAFT:              "bg-slate-100 text-slate-700 border-slate-200",
  ACTIVE:            "bg-emerald-50 text-emerald-800 border-emerald-200",
  EXPIRING_SOON:  "bg-amber-50 text-amber-800 border-amber-200",
  EXPIRED:          "bg-rose-50 text-rose-800 border-rose-200",
  TERMINATED:      "bg-rose-100 text-rose-900 border-rose-300",
  INACTIVE:          "bg-slate-100 text-slate-500 border-slate-200",
};


export default function ContractsWorkspace() {
  const [sp, setSp] = useSearchParams();
  const [meta, setMeta] = useState(null);
  const [rows, setRows] = useState([]);
  const [selectedId, setSelectedId] = useState(sp.get("id") || null);
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState(sp.get("tab") || "list");
  const [showNew, setShowNew] = useState(false);

  useEffect(() => { api.get("/contracts/meta").then(({ data }) => setMeta(data)); }, []);
  const load = useCallback(async () => {
    try { setRows((await api.get("/contracts")).data.items || []); }
    catch (e) { toast.error(e.response?.data?.detail || "Load failed"); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!selectedId) { setSelected(null); return; }
    api.get(`/contracts/${selectedId}`)
      .then(({ data }) => setSelected(data))
      .catch(() => setSelected(null));
  }, [selectedId]);

  const openDetail = (id) => {
    setSelectedId(id); setTab("detail");
    const s = new URLSearchParams(sp); s.set("id", id); s.set("tab", "detail"); setSp(s, { replace: true });
  };

  const activate = async () => {
    try {
      await api.post(`/contracts/${selectedId}/activate`);
      toast.success("Activated"); load();
      api.get(`/contracts/${selectedId}`).then(({ data }) => setSelected(data));
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const terminate = async () => {
    const reason = window.prompt("Termination reason?");
    if (!reason) return;
    try {
      await api.post(`/contracts/${selectedId}/terminate`, { reason });
      toast.success("Terminated"); load();
      api.get(`/contracts/${selectedId}`).then(({ data }) => setSelected(data));
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="max-w-[1500px] mx-auto space-y-4" data-testid="contracts">
      <header className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-emerald-900 text-white grid place-items-center">
            <FileText className="h-5 w-5" />
          </div>
          <div>
            <div className="text-xl font-heading font-semibold">Contracts & Entitlements</div>
            <div className="text-[11px] text-slate-500">
              Customer contracts · service entitlements · SLA policies · renewals
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/commercial/entitlements/renewals"
              data-testid="cx-renewals"
              className="text-[12px] border border-slate-200 rounded-md px-3 py-1.5 hover:bg-slate-50 inline-flex items-center gap-1">
            <CalendarClock className="h-3.5 w-3.5" /> Renewals
          </Link>
          {meta?.current_user?.can_write && (
            <button onClick={() => setShowNew(true)}
                data-testid="cx-new"
                className="text-[12px] px-3 py-1.5 rounded-md bg-slate-900 text-white inline-flex items-center gap-1">
              <Plus className="h-3 w-3" /> New contract
            </button>
          )}
        </div>
      </header>

      <div className="border-b border-slate-200 flex gap-1">
        {[["list", "All contracts"], ["detail", "Selected"], ["policies", "SLA Policies"],
          ["report", "SLA Report"]].map(([k, v]) => (
          <button key={k} onClick={() => setTab(k)}
              data-testid={`cx-tab-${k}`}
              className={"px-3 py-1.5 text-[12px] -mb-px border-b-2 " +
                              (tab === k ? "border-emerald-800 text-emerald-900 font-medium"
                                                        : "border-transparent text-slate-500")}>
            {v}
          </button>
        ))}
      </div>

      {tab === "list" && <ContractList rows={rows} onOpen={openDetail} />}
      {tab === "detail" && (
        selected
          ? <ContractDetail c={selected} onActivate={activate} onTerminate={terminate}
                                              canWrite={meta?.current_user?.can_write}
                                              onChanged={() => { load(); api.get(`/contracts/${selectedId}`).then(({data}) => setSelected(data)); }} />
          : <div className="text-[12px] text-slate-500">Pick a contract from All contracts.</div>
      )}
      {tab === "policies" && <SlaPoliciesTab canWrite={meta?.current_user?.can_write} />}
      {tab === "report" && <SlaReport />}

      {showNew && <NewContractModal meta={meta}
          onClose={() => setShowNew(false)}
          onCreated={(c) => { setShowNew(false); load(); openDetail(c.id); }} />}
    </div>
  );
}


function ContractList({ rows, onOpen }) {
  return (
    <div className="border border-slate-200 rounded-lg bg-white overflow-hidden">
      <table className="min-w-full text-[11px]">
        <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase">
          <tr>
            <th className="p-2 text-left">Name</th>
            <th className="p-2 text-left">Customer</th>
            <th className="p-2 text-left">Type</th>
            <th className="p-2 text-left">Effective</th>
            <th className="p-2 text-left">Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((c) => (
            <tr key={c.id} onClick={() => onOpen(c.id)}
                className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                data-testid={`cx-row-${c.id}`}>
              <td className="p-2 font-semibold">{c.name}
                <span className="text-slate-400 font-mono ml-1">v{c.version}</span>
              </td>
              <td className="p-2">{c.customer_name || c.customer_id?.slice(0, 8)}</td>
              <td className="p-2 font-mono text-[10px]">{c.contract_type}</td>
              <td className="p-2 font-mono text-[10px]">
                {c.effective_from?.slice(0, 10)} → {c.effective_until?.slice(0, 10)}
              </td>
              <td className="p-2">
                <span className={"text-[9px] px-1.5 py-0.5 rounded border " + STATUS_TONE[c.status]}>
                  {c.status}
                </span>
              </td>
            </tr>
          ))}
          {!rows.length && (
            <tr><td colSpan={5} className="p-6 text-center text-slate-400">No contracts yet.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}


function ContractDetail({ c, canWrite, onActivate, onTerminate, onChanged }) {
  const con = c.contract;
  const [showEnt, setShowEnt] = useState(false);
  return (
    <div className="space-y-3" data-testid="cx-detail">
      <div className="flex items-center gap-2">
        <div className="text-[15px] font-heading font-semibold">{con.name}</div>
        <span className={"text-[10px] px-2 py-0.5 rounded border " + STATUS_TONE[con.status]}>
          {con.status}
        </span>
        <span className="text-[10px] font-mono text-slate-500">v{con.version}</span>
        <div className="ml-auto flex items-center gap-1">
          {canWrite && con.status === "DRAFT" && (
            <button onClick={onActivate}
                data-testid="cx-activate"
                className="text-[11px] px-3 py-1 rounded bg-emerald-800 text-white">
              Activate
            </button>
          )}
          {canWrite && ["ACTIVE", "EXPIRING_SOON"].includes(con.status) && (
            <button onClick={onTerminate}
                data-testid="cx-terminate"
                className="text-[11px] px-3 py-1 rounded border border-rose-200 text-rose-700">
              Terminate
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
        <MiniStat k="Customer" v={con.customer_name || con.customer_id?.slice(0, 8)} />
        <MiniStat k="Type" v={con.contract_type} />
        <MiniStat k="Effective" v={con.effective_from?.slice(0, 10) + " → " + con.effective_until?.slice(0, 10)} />
        <MiniStat k="Owner" v={con.contract_owner || "—"} />
      </div>

      <div className="border border-slate-200 rounded-lg bg-white p-3">
        <div className="flex items-center justify-between">
          <div className="text-[11px] font-semibold text-slate-700 flex items-center gap-1">
            <ShieldCheck className="h-3.5 w-3.5 text-emerald-800" /> Entitlements ({c.entitlements?.length || 0})
          </div>
          {canWrite && (
            <button onClick={() => setShowEnt(true)}
                data-testid="cx-add-entitlement"
                className="text-[11px] px-2.5 py-1 rounded border border-slate-200 hover:bg-slate-50 inline-flex items-center gap-1">
              <Plus className="h-3 w-3" /> Add entitlement
            </button>
          )}
        </div>
        <table className="mt-2 min-w-full text-[11px]">
          <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase">
            <tr>
              <th className="p-2 text-left">Service</th>
              <th className="p-2 text-left">Scope</th>
              <th className="p-2 text-left">Limit</th>
              <th className="p-2 text-left">Priority</th>
            </tr>
          </thead>
          <tbody>
            {(c.entitlements || []).map((e) => (
              <tr key={e.id} className="border-t border-slate-100" data-testid={`cx-ent-${e.id}`}>
                <td className="p-2 font-semibold">{e.service_name}</td>
                <td className="p-2 text-[10px] font-mono">{JSON.stringify(e.scope || {})}</td>
                <td className="p-2">{e.limit_kind}{e.included_units ? ` · ${e.included_units}` : ""}</td>
                <td className="p-2">{e.priority}</td>
              </tr>
            ))}
            {!c.entitlements?.length && (
              <tr><td colSpan={4} className="p-4 text-center text-slate-400">No entitlements yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {showEnt && <EntitlementModal contractId={con.id}
          onClose={() => setShowEnt(false)}
          onSaved={() => { setShowEnt(false); onChanged(); }} />}
    </div>
  );
}


function EntitlementModal({ contractId, onClose, onSaved }) {
  const [form, setForm] = useState({
    contract_id: contractId, service_name: "", limit_kind: "UNLIMITED",
    included_units: null, chargeable_beyond_limit: false, priority: "MEDIUM",
    scope: {},
  });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/contracts/entitlements", form);
      toast.success("Entitlement added");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 grid place-items-center p-4" data-testid="cx-ent-modal">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-md p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-[13px] font-heading font-semibold">Add entitlement</div>
          <button onClick={onClose}><XCircle className="h-4 w-4 text-slate-500" /></button>
        </div>
        <input value={form.service_name} onChange={(e) => setForm({ ...form, service_name: e.target.value })}
            placeholder="Service name (e.g. Helpdesk, Asset Recovery)"
            data-testid="cx-ent-svc"
            className="w-full text-[12px] border border-slate-200 rounded-md px-2 py-1.5" />
        <div className="grid grid-cols-2 gap-2">
          <select value={form.limit_kind}
              onChange={(e) => setForm({ ...form, limit_kind: e.target.value })}
              data-testid="cx-ent-limit"
              className="text-[12px] border border-slate-200 rounded-md px-2 py-1.5">
            {["UNLIMITED", "QUANTITY", "USAGE", "INCLUDED"].map((k) =>
              <option key={k} value={k}>{k}</option>)}
          </select>
          <select value={form.priority}
              onChange={(e) => setForm({ ...form, priority: e.target.value })}
              className="text-[12px] border border-slate-200 rounded-md px-2 py-1.5">
            {["CRITICAL", "HIGH", "MEDIUM", "LOW"].map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
          <input type="number" value={form.included_units || ""}
              onChange={(e) => setForm({ ...form, included_units: parseFloat(e.target.value) || null })}
              placeholder="Included units"
              className="text-[12px] border border-slate-200 rounded-md px-2 py-1.5" />
          <label className="flex items-center gap-2 text-[11px]">
            <input type="checkbox" checked={form.chargeable_beyond_limit}
                onChange={(e) => setForm({ ...form, chargeable_beyond_limit: e.target.checked })} />
            Chargeable beyond limit
          </label>
        </div>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="text-[12px] px-3 py-1.5 border rounded">Cancel</button>
          <button onClick={submit} disabled={busy || !form.service_name}
              data-testid="cx-ent-submit"
              className="text-[12px] px-3 py-1.5 rounded bg-emerald-800 text-white disabled:opacity-50">
            {busy ? "Saving…" : "Add"}
          </button>
        </div>
      </div>
    </div>
  );
}


function NewContractModal({ meta, onClose, onCreated }) {
  const [form, setForm] = useState({
    name: "", customer_id: "", customer_name: "",
    contract_type: "ITAM_SERVICES", currency: "INR",
    effective_from: "", effective_until: "",
    contract_owner: "", customer_contact_email: "",
    legal_entity: "", contract_number: "",
    renewal_notice_days: 30, notes: "",
  });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/contracts", form);
      toast.success("Contract created (DRAFT)");
      onCreated(data.contract);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 grid place-items-center p-4" data-testid="cx-new-modal">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-lg p-4 space-y-3 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <div className="text-[13px] font-heading font-semibold">New contract</div>
          <button onClick={onClose}><XCircle className="h-4 w-4 text-slate-500" /></button>
        </div>
        <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Contract name" data-testid="cx-form-name"
            className="w-full text-[12px] border border-slate-200 rounded-md px-2 py-1.5" />
        <div className="grid grid-cols-2 gap-2">
          <input value={form.customer_id} onChange={(e) => setForm({ ...form, customer_id: e.target.value })}
              placeholder="Customer ID" data-testid="cx-form-cust-id"
              className="text-[12px] border border-slate-200 rounded-md px-2 py-1.5" />
          <input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
              placeholder="Customer name"
              className="text-[12px] border border-slate-200 rounded-md px-2 py-1.5" />
          <select value={form.contract_type} onChange={(e) => setForm({ ...form, contract_type: e.target.value })}
              data-testid="cx-form-type"
              className="text-[12px] border border-slate-200 rounded-md px-2 py-1.5">
            {(meta?.contract_types || []).map((t) => <option key={t}>{t}</option>)}
          </select>
          <input value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })}
              placeholder="Currency"
              className="text-[12px] border border-slate-200 rounded-md px-2 py-1.5" />
          <label className="text-[10px] uppercase text-slate-500">Effective from
            <input type="date" value={form.effective_from} onChange={(e) => setForm({ ...form, effective_from: e.target.value })}
                data-testid="cx-form-from"
                className="mt-1 w-full text-[12px] border border-slate-200 rounded-md px-2 py-1.5" />
          </label>
          <label className="text-[10px] uppercase text-slate-500">Effective until
            <input type="date" value={form.effective_until} onChange={(e) => setForm({ ...form, effective_until: e.target.value })}
                data-testid="cx-form-until"
                className="mt-1 w-full text-[12px] border border-slate-200 rounded-md px-2 py-1.5" />
          </label>
          <input value={form.contract_owner} onChange={(e) => setForm({ ...form, contract_owner: e.target.value })}
              placeholder="Owner email"
              className="text-[12px] border border-slate-200 rounded-md px-2 py-1.5" />
          <input type="number" value={form.renewal_notice_days}
              onChange={(e) => setForm({ ...form, renewal_notice_days: parseInt(e.target.value) || 30 })}
              placeholder="Renewal notice (days)"
              className="text-[12px] border border-slate-200 rounded-md px-2 py-1.5" />
        </div>
        <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
            rows={2} placeholder="Notes"
            className="w-full text-[12px] border border-slate-200 rounded-md px-2 py-1.5" />
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="text-[12px] px-3 py-1.5 border rounded">Cancel</button>
          <button onClick={submit} disabled={busy || !form.name || !form.customer_id || !form.effective_from || !form.effective_until}
              data-testid="cx-form-submit"
              className="text-[12px] px-3 py-1.5 rounded bg-slate-900 text-white disabled:opacity-50">
            {busy ? "Creating…" : "Create as DRAFT"}
          </button>
        </div>
      </div>
    </div>
  );
}


function SlaPoliciesTab({ canWrite }) {
  const [rows, setRows] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const load = useCallback(() =>
    api.get("/contracts/sla-policies").then(({ data }) => setRows(data.items || [])), []);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-3" data-testid="cx-policies">
      {canWrite && (
        <button onClick={() => setShowNew(true)}
            data-testid="cx-policy-new"
            className="text-[12px] px-3 py-1.5 rounded-md bg-slate-900 text-white inline-flex items-center gap-1">
          <Plus className="h-3 w-3" /> New SLA policy
        </button>
      )}
      <div className="border border-slate-200 rounded-lg bg-white overflow-hidden">
        <table className="min-w-full text-[11px]">
          <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase">
            <tr>
              <th className="p-2 text-left">Name</th>
              <th className="p-2 text-left">Targets</th>
              <th className="p-2 text-left">Pause reasons</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.id} className="border-t border-slate-100">
                <td className="p-2 font-semibold">{p.name}</td>
                <td className="p-2 text-[10px] font-mono">
                  {(p.targets || []).map((t) => `${t.kind}·${t.priority}=${t.target_hours}h`).join(" · ")}
                </td>
                <td className="p-2 text-[10px]">{(p.pause_reasons_allowed || []).join(", ")}</td>
              </tr>
            ))}
            {!rows.length && (
              <tr><td colSpan={3} className="p-6 text-center text-slate-400">No SLA policies yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {showNew && <SlaPolicyForm onClose={() => setShowNew(false)}
                                                                onCreated={() => { setShowNew(false); load(); }} />}
    </div>
  );
}


function SlaPolicyForm({ onClose, onCreated }) {
  const [form, setForm] = useState({
    name: "Premium Support",
    description: "Response 4h · Resolution 8h business hours",
    targets: [
      { kind: "RESPONSE", priority: "CRITICAL", target_hours: 1 },
      { kind: "RESPONSE", priority: "HIGH", target_hours: 4 },
      { kind: "RESOLUTION", priority: "CRITICAL", target_hours: 4 },
      { kind: "RESOLUTION", priority: "HIGH", target_hours: 8 },
    ],
    pause_reasons_allowed: ["AWAITING_CUSTOMER", "AWAITING_PART"],
  });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/contracts/sla-policies", form);
      toast.success("Policy created");
      onCreated();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 grid place-items-center p-4" data-testid="cx-policy-modal">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-xl p-4 space-y-3">
        <div className="text-[13px] font-heading font-semibold">New SLA policy</div>
        <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Policy name" data-testid="cx-policy-name"
            className="w-full text-[12px] border border-slate-200 rounded-md px-2 py-1.5" />
        <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
            rows={2} className="w-full text-[12px] border border-slate-200 rounded-md px-2 py-1.5" />
        <div className="text-[10px] uppercase text-slate-500">Targets</div>
        {form.targets.map((t, i) => (
          <div key={i} className="grid grid-cols-4 gap-2 items-center" data-testid={`cx-policy-target-${i}`}>
            <select value={t.kind} onChange={(e) => setForm({ ...form,
                                                                                              targets: form.targets.map((x, ix) => ix === i ? { ...x, kind: e.target.value } : x) })}
                className="text-[11px] border border-slate-200 rounded-md px-2 py-1">
              {["RESPONSE", "RESOLUTION", "ACKNOWLEDGE", "PICKUP", "DISPATCH",
                "DEPLOYMENT", "RECOVERY", "OTHER"].map((k) => <option key={k} value={k}>{k}</option>)}
            </select>
            <select value={t.priority} onChange={(e) => setForm({ ...form,
                                                                                              targets: form.targets.map((x, ix) => ix === i ? { ...x, priority: e.target.value } : x) })}
                className="text-[11px] border border-slate-200 rounded-md px-2 py-1">
              {["CRITICAL", "HIGH", "MEDIUM", "LOW"].map((k) => <option key={k} value={k}>{k}</option>)}
            </select>
            <input type="number" value={t.target_hours} onChange={(e) => setForm({ ...form,
                                                                                              targets: form.targets.map((x, ix) => ix === i ? { ...x, target_hours: parseFloat(e.target.value) || 0 } : x) })}
                className="text-[11px] border border-slate-200 rounded-md px-2 py-1" />
            <button onClick={() => setForm({ ...form, targets: form.targets.filter((_, ix) => ix !== i) })}
                className="text-rose-600 text-[11px]">Remove</button>
          </div>
        ))}
        <button onClick={() => setForm({ ...form, targets: [...form.targets,
                                                                                                    { kind: "RESPONSE", priority: "MEDIUM", target_hours: 8 }] })}
            className="text-[11px] text-slate-600 hover:text-slate-900">+ Add target</button>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="text-[12px] px-3 py-1.5 border rounded">Cancel</button>
          <button onClick={submit} disabled={busy}
              data-testid="cx-policy-submit"
              className="text-[12px] px-3 py-1.5 rounded bg-slate-900 text-white">
            {busy ? "Saving…" : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
}


function SlaReport() {
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get("/contracts/sla-report").then(({ data }) => setD(data))
      .catch(() => setD(null));
  }, []);
  if (!d) return <div className="text-[12px] text-slate-500">Loading…</div>;
  return (
    <div className="space-y-3" data-testid="cx-report">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <MiniStat k="Total" v={d.total} />
        <MiniStat k="SLA met" v={d.met_pct != null ? d.met_pct + "%" : "—"} />
        <MiniStat k="Breached" v={d.breached} />
        <MiniStat k="At risk" v={d.at_risk} />
        <MiniStat k="Avg TAT (h)" v={d.avg_tat_hours ?? "—"} />
        <MiniStat k="Median TAT" v={d.median_tat_hours ?? "—"} />
        <MiniStat k="P90 TAT" v={d.p90_tat_hours ?? "—"} />
      </div>
      <div className="text-[10px] text-slate-400">Only ACTUAL metrics — nothing fabricated.</div>
    </div>
  );
}


function MiniStat({ k, v }) {
  return (
    <div className="border border-slate-200 rounded p-2 bg-white">
      <div className="text-[9px] uppercase tracking-wider text-slate-500">{k}</div>
      <div className="text-[14px] font-heading font-semibold truncate">{v ?? "—"}</div>
    </div>
  );
}


/* ================================================================
 * CUSTOMER 360 · Contracts & Entitlements panel
 * ================================================================ */
export function CustomerContractsPanel({ customerId }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    if (!customerId) return;
    api.get(`/contracts/customer/${customerId}/summary`)
        .then(({ data }) => setD(data))
        .catch(() => setD(null));
  }, [customerId]);
  if (!d || (!d.active_contracts?.length && !d.expiring_contracts?.length)) return null;
  return (
    <div className="border border-slate-200 rounded-lg p-3 bg-white" data-testid="cust-contracts-panel">
      <div className="text-[11px] font-semibold text-slate-700 mb-2 flex items-center gap-1">
        <FileText className="h-3.5 w-3.5 text-emerald-800" /> Contracts & Entitlements
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
        <MiniStat k="Active" v={d.active_contracts?.length} />
        <MiniStat k="Expiring soon" v={d.expiring_contracts?.length} />
        <MiniStat k="SLA met %" v={d.sla_performance?.met_pct != null ? d.sla_performance.met_pct + "%" : "—"} />
        <MiniStat k="Cases governed" v={d.sla_performance?.total} />
      </div>
      {d.active_contracts?.length > 0 && (
        <div className="mt-2 space-y-1">
          {d.active_contracts.slice(0, 5).map((c) => (
            <Link key={c.id} to={`/commercial/entitlements?id=${c.id}&tab=detail`}
                className="flex items-center gap-2 text-[11px] p-1.5 rounded hover:bg-slate-50">
              <FileText className="h-3 w-3 text-emerald-800" />
              <span className="flex-1">{c.name}</span>
              <span className="font-mono text-[10px] text-slate-500">
                until {c.effective_until?.slice(0, 10)}
              </span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
